import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigationType, useSearchParams } from "react-router";

import { getArtWarehouseObjects } from "../../api";
import { ArtObjectsList } from "../../components";
import ArtWarehouseSearch from "../../components/ArtWarehouseSearch/ArtWarehouseSearch";
import { useApi } from "../../context/context";

import styles from "./ArtWarehouse.module.scss";

const DEFAULT_PAGE_SIZE = 15;
const DEFAULT_SORT_KEY = "orderInDate";
const DEFAULT_SORT_DIRECTION = "desc";
const getArtWarehouseScrollStorageKey = (search) =>
  `art-warehouse-scroll:${search || ""}`;
const getArtWarehouseListStorageKey = (search) =>
  `art-warehouse-list:${search || ""}`;
const SORT_KEYS = new Set([
  "warehouseName",
  "type",
  "inventoryNo",
  "serialNumber",
  "title",
  "artist",
  "typeOfArt",
  "orderInDate"
]);

const ArtWarehouse = () => {
  const { t } = useTranslation();
  const { companyId, company, user } = useApi();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") || "";
  const initialSortKey = SORT_KEYS.has(searchParams.get("sortKey") || "")
    ? searchParams.get("sortKey")
    : DEFAULT_SORT_KEY;
  const initialSortDirection =
    searchParams.get("dir") === "asc" ? "asc" : DEFAULT_SORT_DIRECTION;
  const initialPage = Math.max(1, Number(searchParams.get("page")) || 1);

  const [searchValue, setSearchValue] = useState(initialSearch);
  const [debouncedSearchValue, setDebouncedSearchValue] =
    useState(initialSearch);
  const [inputFocus, setInputFocus] = useState(false);
  const [opacityClass, setOpacityClass] = useState("noOpacity");
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  const [page, setPage] = useState(initialPage);
  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const previousFiltersRef = useRef(null);
  const hasRestoredScroll = useRef(false);
  const searchRowRef = useRef(null);
  const shouldScrollToListTop = useRef(false);
  const effectiveSearch =
    debouncedSearchValue.trim().length > 0 ? debouncedSearchValue.trim() : "";
  const shouldRestoreScroll =
    !isLoading && !hasRestoredScroll.current && navigationType === "POP";

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    if (inputFocus === true) {
      setOpacityClass("opacity");
    } else {
      setOpacityClass("noOpacity");
    }
  }, [inputFocus]);

  useEffect(() => {
    const previousFilters = previousFiltersRef.current;
    const nextFilters = {
      effectiveSearch,
      sortKey,
      sortDirection
    };

    previousFiltersRef.current = nextFilters;

    if (!previousFilters) {
      return;
    }

    const hasSearchOrSortChanged =
      previousFilters.effectiveSearch !== effectiveSearch ||
      previousFilters.sortKey !== sortKey ||
      previousFilters.sortDirection !== sortDirection;

    if (hasSearchOrSortChanged) {
      setPage(1);
    }
  }, [effectiveSearch, sortKey, sortDirection]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (effectiveSearch) {
      params.set("q", effectiveSearch);
    }

    if (sortKey !== DEFAULT_SORT_KEY) {
      params.set("sortKey", sortKey);
    }

    if (sortDirection !== DEFAULT_SORT_DIRECTION) {
      params.set("dir", sortDirection);
    }

    if (page !== 1) {
      params.set("page", String(page));
    }

    setSearchParams(params, { replace: true });
  }, [effectiveSearch, page, setSearchParams, sortDirection, sortKey]);

  useEffect(() => {
    const savedList = sessionStorage.getItem(
      getArtWarehouseListStorageKey(location.search)
    );

    if (!savedList) return;

    try {
      const parsed = JSON.parse(savedList);

      if (Array.isArray(parsed?.items)) {
        setItems(parsed.items);
      }

      if (Number(parsed?.totalPages) > 0) {
        setTotalPages(parsed.totalPages);
      }
    } catch (error) {
      console.warn("Failed to restore art warehouse list cache", error);
    }
  }, [location.search]);

  useEffect(() => {
    hasRestoredScroll.current = false;
  }, [location.search]);

  useEffect(() => {
    const { history } = window;
    const previousValue = history.scrollRestoration;

    history.scrollRestoration = "manual";

    return () => {
      history.scrollRestoration = previousValue;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadObjects = async () => {
      setIsLoading(true);

      try {
        const response = await getArtWarehouseObjects({
          companyId,
          token: user?.token,
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          search: effectiveSearch,
          sortKey,
          sortDirection
        });

        if (!isMounted) return;

        setItems(response.items);
        setTotalPages(response.totalPages);
        sessionStorage.setItem(
          getArtWarehouseListStorageKey(location.search),
          JSON.stringify({
            items: response.items,
            totalPages: response.totalPages
          })
        );

        if (response.page !== page) {
          setPage(response.page);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadObjects();

    return () => {
      isMounted = false;
    };
  }, [
    companyId,
    effectiveSearch,
    location.search,
    page,
    sortKey,
    sortDirection,
    user?.token
  ]);

  useEffect(() => {
    if (!shouldRestoreScroll) {
      return;
    }

    const storageKey = getArtWarehouseScrollStorageKey(location.search);
    const savedScroll = sessionStorage.getItem(storageKey);

    hasRestoredScroll.current = true;

    if (!savedScroll) return;

    const top = Number(savedScroll);

    requestAnimationFrame(() => {
      window.scrollTo({ top, behavior: "auto" });
    });
  }, [items.length, location.search, shouldRestoreScroll]);

  useEffect(() => {
    if (isLoading || !shouldScrollToListTop.current || shouldRestoreScroll) {
      return;
    }

    shouldScrollToListTop.current = false;

    requestAnimationFrame(() => {
      searchRowRef.current?.scrollIntoView({
        block: "start",
        behavior: "smooth"
      });
    });
  }, [isLoading, shouldRestoreScroll, items.length]);

  const handleSort = (nextKey) => {
    if (nextKey === sortKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(nextKey);
      setSortDirection("asc");
    }
  };

  const currentPage = Math.min(page, totalPages);
  const companyName = company?.Name || "";
  const goToPage = (nextPage) => {
    shouldScrollToListTop.current = true;
    setPage(nextPage);
  };

  return (
    <div className={`${styles.artWarehousePage} ${styles[opacityClass]}`}>
      <Typography variant="h1" color="primary.main">
        {t("artWarehouse")}
      </Typography>

      <div className={styles.searchRow} ref={searchRowRef}>
        <div className={styles.searchWrap}>
          <ArtWarehouseSearch
            searchValue={searchValue}
            setInputFocus={setInputFocus}
            setSearchValue={setSearchValue}
            setOpacityClass={setOpacityClass}
            placeholder="Search Object"
          />
        </div>
        {isLoading ? (
          <div className={styles.loadingSpinner}>
            <CircularProgress size={20} />
          </div>
        ) : null}
      </div>

      <ArtObjectsList
        objects={items}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      <div className={styles.paginationFooter}>
        <div className={styles.footerSpacer} aria-hidden="true" />

        <div className={styles.pageButtons}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => goToPage(Math.max(currentPage - 1, 1))}
            disabled={isLoading || currentPage <= 1}
          >
            <ChevronLeftIcon /> Back
          </Button>

          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>

          <Button
            variant="outlined"
            size="small"
            onClick={() => goToPage(Math.min(currentPage + 1, totalPages))}
            disabled={isLoading || currentPage >= totalPages}
          >
            Next <ChevronRightIcon />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ArtWarehouse;
